import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import i18n, { t } from "@/i18n";
import { Button, Card, Field, Input, Textarea, Select, Badge, Spinner } from "@/components/ui";
import { useAssignmentTypes, useCreateAssignment, translationFor } from "@/lib/queries";

export const Route = createFileRoute("/app/assignments_/new")({
  validateSearch: (search: Record<string, unknown>) => ({
    type: typeof search.type === "string" ? search.type : undefined,
  }),
  component: NewAssignmentPage,
});

function NewAssignmentPage() {
  const { type: typeCode } = Route.useSearch();
  const navigate = useNavigate();
  const { data: types, isLoading } = useAssignmentTypes();
  const selected = (types ?? []).find((x) => x.id === typeCode);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("new.title")}</h1>
        <Link to="/app/assignments">
          <Button variant="ghost">← {t("common.back")}</Button>
        </Link>
      </div>

      {isLoading ? (
        <Spinner label={t("app.loading")} />
      ) : !selected ? (
        <TypePicker
          types={types ?? []}
          onPick={(id) => navigate({ search: { type: id }, to: "/app/assignments/new" })}
        />
      ) : (
        <MetaForm typeId={selected.id} />
      )}
    </div>
  );
}

function TypePicker({
  types,
  onPick,
}: {
  types: Awaited<ReturnType<typeof useAssignmentTypes>["data"]> | undefined;
  onPick: (typeId: string) => void;
}) {
  const uiLang = i18n.language?.slice(0, 2) || "en";
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t("new.pickType")}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(types ?? []).map((x) => {
          const tr = translationFor(x, uiLang);
          return (
            <button
              key={x.id}
              onClick={() => onPick(x.id)}
              className="rounded-xl border bg-card p-4 text-left transition-colors hover:border-primary"
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold">{tr?.name ?? x.code}</span>
                <Badge>{x.code}</Badge>
              </div>
              {tr?.description && (
                <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
                  {tr.description}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MetaForm({ typeId }: { typeId: string }) {
  const navigate = useNavigate();
  const create = useCreateAssignment();
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [languageCode, setLanguageCode] = useState("es");
  const [tags, setTags] = useState("");
  const [error, setError] = useState("");

  async function submit(status: "draft" | "ready") {
    if (!title.trim()) {
      setError(t("new.titleRequired"));
      return;
    }
    try {
      const created = await create.mutateAsync({
        assignmentTypeId: typeId,
        title: title.trim(),
        instructions,
        languageCode,
        tags,
        status,
      });
      await navigate({ to: "/app/assignments/$id", params: { id: created.id } });
    } catch (e) {
      setError(e instanceof Error ? e.message : "error");
    }
  }

  return (
    <Card className="max-w-2xl space-y-4 p-6">
      <Field label={t("new.titleLabel")}>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("assignments.titlePlaceholder")}
        />
      </Field>
      <Field label={t("new.instructionsLabel")}>
        <Textarea
          rows={4}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder={t("assignments.instructionsPlaceholder")}
        />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label={t("new.languageLabel")}>
          <Select value={languageCode} onChange={(e) => setLanguageCode(e.target.value)}>
            <option value="en">English</option>
            <option value="es">Español</option>
          </Select>
        </Field>
        <Field label={t("new.tagsLabel")}>
          <Input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder={t("new.tagsPlaceholder")}
          />
        </Field>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button disabled={create.isPending} onClick={() => void submit("draft")}>
          {t("new.createDraft")}
        </Button>
        <Button variant="outline" disabled={create.isPending} onClick={() => void submit("ready")}>
          {t("new.createReady")}
        </Button>
      </div>
    </Card>
  );
}